<script setup lang="ts">
  defineOptions({ name: 'MyCoursesNavbarSearch' })

  import { computed, h, onMounted, onUnmounted, ref, watch } from 'vue'

  import { useRoute } from 'nuxt/app'
  import { useI18n } from 'vue-i18n'

  import { createElementId } from '@common/core-layer/utils/core.utils'

  import { useLogger } from '@common/core-layer/composables/useLogger'

  import { getLocalStorageItem } from '@common/core-layer/utils/storage.helper'

  import SearchBar from '@courses/search-layer/components/SearchBar.vue'
  import SearchResults from '@courses/search-layer/components/SearchResults.vue'
  import { COURSES_SEARCH_HISTORY_KEY } from '@courses/search-layer/constants/search-history'

  import CPDropdown from '@ui/components/CPDropdown.vue'

  const SEARCH_DEBOUNCE_MS = 300

  const route = useRoute()
  const { t } = useI18n()
  const { logger } = useLogger()

  const dropdownId = createElementId('my-courses', 'search', 'dropdown')
  const dropdownMenuId = `${dropdownId}-menu`

  const containerRef = ref<HTMLElement | null>(null)
  const dropdownMenuWidth = ref(0)
  let resizeObserver: ResizeObserver | null = null

  const isDropdownOpen = ref(false)
  const searchQuery = ref('')
  const searchResultsQuery = ref('')

  const updateDropdownMenuWidth = () => {
    dropdownMenuWidth.value = containerRef.value?.clientWidth ?? 0
  }

  const hasRecentSearches = () => {
    const stored = getLocalStorageItem(COURSES_SEARCH_HISTORY_KEY)
    if (!stored) return false

    try {
      const parsed: unknown = JSON.parse(stored)
      return Array.isArray(parsed) && parsed.some(item => typeof item === 'string' && item.trim())
    } catch (error) {
      logger.warn('Unable to parse courses search history', {
        error: error instanceof Error ? error : new Error(String(error)),
      })
      return false
    }
  }

  const canOpenDropdown = (query = searchQuery.value) => Boolean(query.trim() || hasRecentSearches())

  const openDropdown = () => {
    if (!canOpenDropdown()) return
    isDropdownOpen.value = true
  }

  const closeDropdown = () => {
    isDropdownOpen.value = false
  }

  const handleDropdownVisibility = (show: boolean) => {
    if (show) {
      openDropdown()
      return
    }
    closeDropdown()
  }

  const handleQueryUpdate = (query: string) => {
    searchQuery.value = query
    if (canOpenDropdown(query)) {
      isDropdownOpen.value = true
      return
    }
    closeDropdown()
  }

  const handleSelectQuery = (query: string) => {
    searchQuery.value = query
    searchResultsQuery.value = query
    openDropdown()
  }

  const handleSubmit = (query: string) => {
    if (query?.trim()) {
      searchQuery.value = query
      searchResultsQuery.value = query
    }
    openDropdown()
  }

  const handleClear = () => {
    searchQuery.value = ''
    searchResultsQuery.value = ''
    closeDropdown()
    openDropdown()
  }

  const handleRecentSearchesChange = (remainingCount: number) => {
    if (remainingCount === 0 && !searchQuery.value.trim()) {
      closeDropdown()
    }
  }

  const dropdownOptions = computed(() => {
    const query = searchResultsQuery.value

    return [
      {
        key: 'courses-search-results',
        type: 'render' as const,
        render: () =>
          h(SearchResults, {
            query,
            variant: 'dropdown',
            onClear: handleClear,
            onRecentSearchesChange: handleRecentSearchesChange,
            onSelectQuery: handleSelectQuery,
          }),
      },
    ]
  })

  onMounted(() => {
    updateDropdownMenuWidth()
    if (!containerRef.value || typeof ResizeObserver === 'undefined') return

    resizeObserver = new ResizeObserver(() => {
      updateDropdownMenuWidth()
    })
    resizeObserver.observe(containerRef.value)
  })

  onUnmounted(() => {
    resizeObserver?.disconnect()
    resizeObserver = null
  })

  watch(searchQuery, (query, _previousQuery, onCleanup) => {
    const timeoutId = setTimeout(() => {
      searchResultsQuery.value = query
    }, SEARCH_DEBOUNCE_MS)

    onCleanup(() => clearTimeout(timeoutId))
  })

  watch(
    () => route.query.q,
    value => {
      const routeQuery = typeof value === 'string' ? value : ''
      searchQuery.value = routeQuery
      searchResultsQuery.value = routeQuery
      closeDropdown()
    },
    { immediate: true }
  )

  watch(() => route.fullPath, closeDropdown)
</script>

<template>
  <div :id="createElementId('my-courses', 'search', 'container')" ref="containerRef" class="w-full relative">
    <CPDropdown
      :id="dropdownId"
      :show="isDropdownOpen"
      :options="dropdownOptions"
      class="w-full"
      :width="dropdownMenuWidth"
      :show-arrow="false"
      :show-search="false"
      :close-on-select="false"
      max-height="70vh"
      placement="bottom-start"
      @clickoutside="closeDropdown"
      @keydown.esc="closeDropdown"
      @update:show="handleDropdownVisibility"
    >
      <SearchBar
        behavior="local"
        :model-value="searchQuery"
        :aria-controls="dropdownMenuId"
        :aria-expanded="isDropdownOpen"
        :aria-label="t('courses.search.searchBar.placeholder')"
        class="w-full"
        @focus="openDropdown"
        @submit="handleSubmit"
        @update:model-value="handleQueryUpdate"
      />
    </CPDropdown>
  </div>
</template>

<style scoped>
  :deep(#my-courses-search-dropdown-trigger) {
    display: block;
    position: absolute;
    top: 50%;
    width: 100%;
    left: 0%;
    transform: translateY(-50%);
  }
</style>
